import "@assets/css/bootstrap-grid.css";
import { Spinner } from "@components/ui/spinner";
import { zodResolver } from "@hookform/resolvers/zod";
import { endpoint } from "@services/api.services";
import { METHODS } from "@services/interface.service";
import { useQueryClient } from "@tanstack/react-query";
import { SkeletonTwo } from "@ui/external/components/skeleton";
import { getInputField } from "@utils/function/index.function";
import { showToast, throwErrorAlert } from "@utils/helper/index.helper";
import { useHandleAction, usePathname } from "@utils/hook/index.hook";
import { MomentsFormData, MomentsSchema } from "@utils/interface/form.schema";
import { IndividualQuery } from "@utils/interface/index.interface";
import { useListData, usePostData } from "@utils/query/index.query";
import { usePending, useSelectionStore } from "@utils/states/index.state";
import React, { useEffect, useMemo } from "react";
import { SubmitHandler, useForm } from "react-hook-form";
import { useParams } from "react-router";
import { toast } from "sonner";

const MomentsForm: React.FC = () => {
  const queryClient = useQueryClient();
  const { searchParams, handleAction } = useHandleAction({});
  const { type } = useParams();
  const modalItem = searchParams.get("modal-item");

  const pathname = usePathname();
  const {
    handleSubmit,
    control,
    setValue,
    reset,
    formState: { errors },
  } = useForm<MomentsFormData>({
    mode: "onChange",
    resolver: zodResolver(MomentsSchema),
    defaultValues: {
      title: modalItem === "add" ? (searchParams.get("search") as string) : "",
    },
  });
  console.log("🚀 ~ errors:", errors);

  /* set default value */
  const { setIsRetrive }: any = usePending();
  const { setIsFormPending } = useSelectionStore();
  const { data, isPending: fetchPending } = useListData<
    IndividualQuery<MomentsFormData>
  >({
    endpoint: endpoint["moments"],
    id: modalItem || "",
    key: "moments" + modalItem,
    enabled: modalItem ? true : false,
  });

  useEffect(() => {
    setIsRetrive(modalItem ? fetchPending : false);
    if (searchParams.get("modal") == "add") {
      return reset({});
    }

    if (data && searchParams.get("modal") !== "add") {
      return reset({
        thumbnail: data?.data?.thumbnail,
        video: data?.data?.video,
        title: data?.data?.title,
        header_code: data?.data?.header_code || "",
        footer_code: data?.data?.footer_code || "",
      });
    }
  }, [data, fetchPending]);

  const { mutate, isPending } = usePostData();

  /* mutate action */
  const handleMutate = (data: any) => {
    setIsFormPending(true);

    if (modalItem) {
      data = {
        ...data,
        id: modalItem,
      };
    }
    //   if (!(data.thumbnail instanceof File || data.thumbnail instanceof Blob))
    //     delete data.thumbnail;
    // }
    // if (!(data.video instanceof File || data.video instanceof Blob))
    //   delete data.video;

    return {
      data,
      endpoint: endpoint["moments"],
      method: modalItem ? METHODS.PUT : METHODS.POST,
    };
  };

  const handleSuccess = async ({ status }: { data: any; status: number }) => {
    if ([200, 201].includes(status)) {
      queryClient.invalidateQueries({ queryKey: [`${pathname}-listing`] });
      queryClient.invalidateQueries({
        queryKey: [`${pathname}-listing-scroll`],
      });
      setIsFormPending(false);
      handleAction({ type: "modal-close" });
      showToast({
        description: `Moments ${status === 200 ? "Updated" : "Created"}`,
        type: "success",
        title: "Success",
      });
    } else {
      return new Error("Unknown API Error");
    }
  };
  const handleError = async (error: any) => {
    setIsFormPending(false);

    throwErrorAlert(error);
  };
  const onSubmit: SubmitHandler<MomentsFormData> = (data) => {
    mutate(handleMutate(data), {
      onSuccess: ({ data, status }) => handleSuccess({ data, status }),
      onError: (data) => handleError({ data }),
    });
  };

  const formSchema = useMemo(
    () => [
      {
        name: "title",
        label: "Title",
        colSpan: 12,
        type: "text",
        placeholder: "Enter Title",
        control: control,
        setValue: setValue,
        errors,
        className: `mb-[20px] min-w-full w-full`,
        visable: true,
      },
      {
        name: "thumbnail",
        label: "Upload Image",
        colSpan: 6,
        type: "cropper",
        cropperWidthPlaceholder: 542,
        cropperHeightPlaceholder: 714,
        control: control,
        setValue: setValue,
        errors,
        className: `mb-[20px] min-w-full w-full`,
        visable: true,
      },
      {
        name: "video",
        label: "Upload Videos",
        upload_type: "video",
        colSpan: 6,
        type: "cropper",
        cropperWidthPlaceholder: 542,
        cropperHeightPlaceholder: 714,
        control: control,
        setValue: setValue,
        errors,
        className: `mb-[20px] min-w-full w-full`,
        visable: true,
      },
      {
      name: "header_code",
      label: "Header Code ",
      colSpan: 12,
      type: "textarea",
      placeholder: "Description Here",
      control: control,
      setValue: setValue,
      errors,
      className: `mb-[20px] min-w-full  w-full`,
      visable: true,
    },
    {
      name: "footer_code",
      label: "Footer Code ",
      colSpan: 12,
      type: "textarea",
      placeholder: "Description Here",
      control: control,
      setValue: setValue,
      errors,
      className: `mb-[20px] min-w-full  w-full`,
      visable: true,
    },
      {
        type: "submit",
        value: `${searchParams.get("modal") || ""} ${(
          type || pathname
        )?.replace("-", " ")}`,
        className: "w-full h-[47px] mt-[32px] capitalize",
        colSpan: 12,
        variant: "default",

        endIcon: isPending ? <Spinner /> : null,
        disabled: isPending ? true : false,
        visable: true,
      },
    ],
    [control, setValue, errors, isPending]
  );

  if (fetchPending && modalItem)
    return (
      <>
        <SkeletonTwo />
      </>
    );
  return (
    <>
      <form onSubmit={handleSubmit(onSubmit)} className="row w-full">
        {formSchema.map(
          (field, index) =>
            (field.visable || field.visable == undefined) && (
              <div
                key={index}
                className={`col-lg-${field.colSpan} col-12 lg:px-[12px] px-[8px]`}
              >
                {getInputField({ ...field })}
              </div>
            )
        )}
      </form>
    </>
  );
};

export default MomentsForm;
